/* eslint-disable */
import pino from 'pino';
import path from 'path';
import fs from 'fs';

// Check if we're in Node.js environment
const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;

let logToFile: ((level: string, message: string, data?: any) => void) | undefined;

// Only set up file logging in Node.js environment
if (isNode) {
  
  // Ensure logs directory exists
  const logsDir = path.join(process.cwd(), 'logs');
  if (!fs.existsSync(logsDir)) {
    fs.mkdirSync(logsDir, { recursive: true });
  }

  // Create date-based log file name
  const today = new Date().toISOString().split('T')[0];
  const logFile = path.join(logsDir, `${today}-combined.log`);

  // File logging function (only available in Node.js)
  logToFile = (level: string, message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    const logEntry = `${timestamp} [${level.toUpperCase()}] ${message}${data ? ' ' + JSON.stringify(data) : ''}\n`;
    fs.appendFileSync(logFile, logEntry);
  };
}

/**
 * Преобразует значение ошибки к сериализуемому объекту.
 */
function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const base: Record<string, unknown> = {
      name: error.name,
      message: error.message,
    };
    if (error.stack) {
      base.stack = error.stack;
    }
    const cause = (error as Error & { cause?: unknown }).cause;
    if (cause !== undefined) {
      base.cause = serializeError(cause);
    }
    const extra = error as unknown as Record<string, unknown>;
    for (const key of Object.keys(extra)) {
      if (!(key in base)) {
        base[key] = extra[key];
      }
    }
    return base;
  }
  return { message: String(error) };
}

/**
 * Рекурсивно нормализует значения для безопасного логирования.
 */
function transformValue(value: unknown): unknown {
  if (value instanceof Error) {
    return serializeError(value);
  }
  if (Array.isArray(value)) {
    return value.map(transformValue);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, val]) => [
      key,
      transformValue(val),
    ]);
    return Object.fromEntries(entries);
  }
  return value;
}

/**
 * Приводит данные лога к объекту (если возможно) и конвертирует ошибки.
 */
function normalizeData(data?: unknown): Record<string, unknown> | undefined {
  if (data === undefined || data === null) {
    return undefined;
  }
  const transformed = transformValue(data);
  if (transformed && typeof transformed === 'object' && !Array.isArray(transformed)) {
    return transformed as Record<string, unknown>;
  }
  return { value: transformed };
}

// Create logger with environment-appropriate configuration
function buildTransport(): pino.TransportSingleOptions | pino.TransportMultiOptions | undefined {
  if (!isNode) return undefined;

  const targets: pino.TransportTargetOptions[] = [
    {
      target: 'pino-pretty',
      options: {
        colorize: true,
        translateTime: 'yyyy-mm-dd HH:MM:ss',
        ignore: 'pid,hostname',
      },
    },
  ];

  const lokiUrl = process.env.LOKI_URL;
  const nodeEnv = process.env.NODE_ENV;
  if (lokiUrl && nodeEnv !== 'development' && nodeEnv !== 'test') {
    targets.push({
      target: 'pino-loki',
      options: {
        host: lokiUrl,
        labels: {
          app: 'aya',
          instance: process.env.INSTANCE_NAME || 'unknown',
        },
        batching: true,
        interval: 5,
      },
    });
  }

  return { targets };
}

const logger = pino({
  level: 'info',
  transport: buildTransport(),
});

// Enhanced logger with conditional file logging
const enhancedLogger = {
  ...logger,
  
  // Override methods to add file logging (only in Node.js)
  info: (message: string, data?: any) => {
    const payload = normalizeData(data);
    payload ? logger.info(payload, message) : logger.info(message);
    if (logToFile) logToFile('info', message, payload);
  },
  
  error: (first: unknown, second?: unknown) => {
    let logMessage = 'Unhandled error';
    let payload: Record<string, unknown> | undefined;

    if (typeof first === 'string') {
      logMessage = first;
      payload = normalizeData(second);
    } else if (first instanceof Error) {
      const errorPayload = serializeError(first);
      logMessage = errorPayload.message ? String(errorPayload.message) : first.message;
      const extra = normalizeData(second);
      payload = extra ? { ...errorPayload, ...extra } : errorPayload;
    } else {
      const firstPayload = normalizeData(first);
      const extra = normalizeData(second);
      payload = firstPayload;
      if (extra) {
        payload = payload ? { ...payload, ...extra } : extra;
      }
    }

    payload ? logger.error(payload, logMessage) : logger.error(logMessage);
    if (logToFile) logToFile('error', logMessage, payload);
  },
  
  debug: (message: string, data?: any) => {
    const payload = normalizeData(data);
    payload ? logger.debug(payload, message) : logger.debug(message);
    if (logToFile) logToFile('debug', message, payload);
  },
  
  warn: (message: string, data?: any) => {
    const payload = normalizeData(data);
    payload ? logger.warn(payload, message) : logger.warn(message);
    if (logToFile) logToFile('warn', message, payload);
  },
  
  // WebSocket specific logging
  websocket: (message: string, data?: any) => {
    const fullMessage = `[WebSocket] ${message}`;
    const payload = normalizeData({ category: 'websocket', ...data });
    payload ? logger.debug(payload, fullMessage) : logger.debug(fullMessage);
    if (logToFile) logToFile('websocket', fullMessage, payload);
  },
  
  // Messaging specific logging
  messaging: (message: string, data?: any) => {
    const fullMessage = `[Messaging] ${message}`;
    const payload = normalizeData({ category: 'messaging', ...data });
    payload ? logger.info(payload, fullMessage) : logger.info(fullMessage);
    if (logToFile) logToFile('messaging', fullMessage, payload);
  },
  
  // Queue specific logging
  queue: (message: string, data?: any) => {
    const fullMessage = `[Queue] ${message}`;
    const payload = normalizeData({ category: 'queue', ...data });
    payload ? logger.debug(payload, fullMessage) : logger.debug(fullMessage);
    if (logToFile) logToFile('queue', fullMessage, payload);
  },

  /** Создаёт дочерний pino-логгер с привязанными полями (наследует транспорты) */
  child: (bindings: Record<string, unknown>) => logger.child(bindings),
};

export default enhancedLogger;
